import { Box, Dialog } from "@mui/material";
import { useEffect, useState } from "react";
import { useLazyQuery } from "@apollo/client";
import WarrantyStartPopup from "./WarrantyStartPopup.component";
import WarrantyChart from "./WarrantyChart.component";
import { FETCH_WARRANTY_COVERAGES } from "../../../../../graphql/query/query";
import { useVerification } from "../../../../../context/Verification.context";
import { WARRANTY_VEHICLE_TYPE_ID } from "../../../admin/dealership/tabs/warrantyConstants";

const ENGINE_TO_WARRANTY_TYPE_ID = {
  GAS: WARRANTY_VEHICLE_TYPE_ID.GAS,
  HYBRID: WARRANTY_VEHICLE_TYPE_ID.HYBRID,
  EV: WARRANTY_VEHICLE_TYPE_ID.EV,
  DIESEL: WARRANTY_VEHICLE_TYPE_ID.DIESEL,
};

/**
 * Flow (mobile AND desktop, same behavior now):
 *  - Always show WarrantyStartPopup first (rotate + Start on mobile, just Start on desktop).
 *  - Chart only renders after the user clicks Start.
 * Chart is always fullscreen; on mobile portrait it is CSS-rotated into landscape.
 */
const WarrantyPresentation = ({
  isPortraitMobile,
  onDismiss,
}) => {
  const verification = useVerification();
  const [showChart, setShowChart] = useState(false);
  const [vehicleTypeId, setVehicleTypeId] = useState(null);
  const [coverages, setCoverages] = useState([]);
  const [exclusions, setExclusions] = useState([]);

  const [fetchCoverages, { loading }] = useLazyQuery(FETCH_WARRANTY_COVERAGES, {
    fetchPolicy: "network-only",
  });

  const engineKey =
    verification?.artifactsResponse?.vehicleTransactionResponse?.engine ||
    null;
  const resolvedTypeId = engineKey
    ? ENGINE_TO_WARRANTY_TYPE_ID[engineKey] || null
    : null;

  useEffect(() => {
    if (!showChart) return undefined;

    const lockLandscape = async () => {
      try {
        await window.screen?.orientation?.lock?.("landscape");
      } catch {
        // Not supported / requires fullscreen gesture — CSS fallback handles it.
      }
    };

    lockLandscape();

    return () => {
      try {
        window.screen?.orientation?.unlock?.();
      } catch {
        // ignore
      }
    };
  }, [showChart]);

  const handleStart = async (typeId) => {
    if (loading) return;

    setVehicleTypeId(typeId);

    const { data } = await fetchCoverages({
      variables: {
        warrantyCoverageInput: {
          dealership_id: parseInt(verification?.dealershipId, 10),
          warranty_vehicle_type_id: typeId,
        },
      },
    });

    setCoverages(data?.fetchWarrantyCoverages?.coverages || []);
    setExclusions(data?.fetchWarrantyCoverages?.exclusions || []);
    setShowChart(true);
  };

  const resetAndDismiss = () => {
    setShowChart(false);
    setVehicleTypeId(null);
    setCoverages([]);
    setExclusions([]);
    onDismiss?.();
  };

  if (showChart) {
    return (
      <Dialog
        open
        fullScreen
        onClose={resetAndDismiss}
        PaperProps={{
          sx: {
            backgroundColor: "#fff",
            display: "flex",
            flexDirection: "column",
            m: 0,
            width: "100%",
            height: "100%",
            maxWidth: "100%",
            maxHeight: "100%",
            overflow: "hidden",
          },
        }}
      >
        <Box
          sx={{
            flex: 1,
            minHeight: 0,
            width: "100%",
            height: "100%",
            overflow: "hidden",
            "@media (orientation: portrait) and (max-width: 900px)": {
              position: "fixed",
              top: 0,
              left: 0,
              width: "100vh",
              height: "100vw",
              transform: "rotate(90deg) translateY(-100%)",
              transformOrigin: "top left",
              overflow: "hidden",
            },
          }}
        >
          <WarrantyChart
            coverages={coverages}
            exclusions={exclusions}
            vehicleTypeId={vehicleTypeId}
            onNext={resetAndDismiss}
          />
        </Box>
      </Dialog>
    );
  }

  return (
    <WarrantyStartPopup
      isPortraitMobile={isPortraitMobile}
      onStart={() => handleStart(resolvedTypeId)}
      onStop={resetAndDismiss}
      loading={loading}
    />
  );
};

export default WarrantyPresentation;